import { useEffect, useState } from "react";
import { adminApi, adminUploadFile } from "@/lib/api";

const inputCls = "w-full mt-1 px-3 py-2 border border-[#F1DCC5] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm";
const labelCls = "font-badge text-[10px] tracking-[0.22em] text-[#8A7568]";
const DRAFT_KEY = "gl_newsletter_draft";
const EMPTY = { subject: "", heading: "", body: "", image_url: "", cta_label: "Ver a loja", cta_url: "/collection/all" };
const errText = (e, f) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);

const Field = ({ label, k, d, setD, ...rest }) => (
  <label className="block">
    <span className={labelCls}>{label}</span>
    <input data-testid={`newsletter-${k}`} value={d[k] || ""} onChange={(e) => setD({ ...d, [k]: e.target.value })} className={inputCls} {...rest} />
  </label>
);

const History = ({ rows }) =>
  rows.length ? (
    <div className="mt-6" data-testid="newsletter-history">
      <div className={labelCls}>SENT NEWSLETTERS</div>
      <ul className="mt-2 divide-y divide-[#F1DCC5] text-sm">
        {rows.map((r) => (
          <li key={r.id} className="py-2 flex justify-between gap-3">
            <span className="truncate">{r.subject}</span>
            <span className="text-xs text-[#8A7568] shrink-0">
              {new Date(r.created_at).toLocaleString("pt-PT")} · {r.status === "sending" ? "sending…" : r.status} · {r.sent}/{r.recipients}
              {r.failed ? ` · ${r.failed} failed` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

export const NewsletterComposer = ({ activeCount }) => {
  const [d, setD] = useState(() => {
    try { return { ...EMPTY, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") }; } catch (_e) { return EMPTY; }
  });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => adminApi.get("/admin/newsletter/campaigns").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);
  useEffect(() => { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); }, [d]);
  useEffect(() => {
    if (!history.some((h) => h.status === "sending")) return undefined;
    const id = setInterval(loadHistory, 4000);
    return () => clearInterval(id);
  }, [history]);

  const run = async (kind, fn, ok) => {
    setBusy(kind); setMsg(null);
    try { await fn(); setMsg({ text: ok }); } catch (e) { setMsg({ err: true, text: errText(e, "Something went wrong") }); } finally { setBusy(null); }
  };
  const upload = (file) => file && run("upload", async () => {
    const { url } = await adminUploadFile(file);
    setD((prev) => ({ ...prev, image_url: url }));
  }, "Image added.");
  const sendTest = () => run("test", () => adminApi.post("/admin/newsletter/test", { ...d, to: testTo }), `Test sent to ${testTo}.`);
  const sendAll = () => {
    if (!window.confirm(`Send "${d.subject}" to ${activeCount} subscribers now? This can't be undone.`)) return;
    run("all", async () => { await adminApi.post("/admin/newsletter/send", d); await loadHistory(); }, "Sending started — progress shows below.");
  };

  return (
    <section className="bg-white border border-[#F1DCC5] rounded-2xl p-6 mb-8" data-testid="newsletter-composer">
      <h2 className="font-serif-display text-2xl text-[#241C18]">Write a newsletter</h2>
      <p className="text-xs text-[#8A7568] mt-1 mb-4">Sent with the GOYA email design. An unsubscribe link is added automatically.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="SUBJECT *" k="subject" d={d} setD={setD} placeholder="Black Friday: até 30% 🖤" />
        <Field label="TITLE IN THE EMAIL" k="heading" d={d} setD={setD} placeholder="A Black Friday chegou" />
        <label className="block md:col-span-2">
          <span className={labelCls}>TEXT (blank line = new paragraph)</span>
          <textarea data-testid="newsletter-body" rows={7} value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} className={inputCls} />
        </label>
        <div className="md:col-span-2">
          <span className={labelCls}>IMAGE (optional)</span>
          <div className="flex items-center gap-3 mt-1 flex-wrap">
            {d.image_url && <img src={d.image_url} alt="" className="h-20 w-auto rounded border border-[#F1DCC5]" data-testid="newsletter-image-preview" />}
            <label className="px-4 py-2 rounded-full border border-[#F1DCC5] text-xs font-badge tracking-[0.14em] uppercase cursor-pointer hover:bg-[#F1DCC5]/50">
              {busy === "upload" ? "Uploading…" : d.image_url ? "Change image" : "Upload image"}
              <input data-testid="newsletter-image-input" type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
            </label>
            {d.image_url && <button data-testid="newsletter-image-remove" onClick={() => setD({ ...d, image_url: "" })} className="text-xs underline text-[#8A7568]">Remove</button>}
          </div>
        </div>
        <Field label="BUTTON TEXT" k="cta_label" d={d} setD={setD} />
        <Field label="BUTTON LINK" k="cta_url" d={d} setD={setD} placeholder="/collection/all" />
      </div>
      <div className="mt-5 flex flex-col md:flex-row md:items-end gap-3">
        <label className="block flex-1">
          <span className={labelCls}>SEND A TEST TO</span>
          <input data-testid="newsletter-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className={inputCls} placeholder="you@email.com" />
        </label>
        <button data-testid="newsletter-send-test" onClick={sendTest} disabled={!!busy || !d.subject || !testTo} className="px-4 py-2 rounded-full border border-[#241C18] text-xs font-badge tracking-[0.14em] uppercase disabled:opacity-40">
          {busy === "test" ? "Sending…" : "Send test"}
        </button>
        <button data-testid="newsletter-send-all" onClick={sendAll} disabled={!!busy || !d.subject || !activeCount} className="btn-orange text-xs disabled:opacity-40">
          {busy === "all" ? "Starting…" : `Send to ${activeCount} subscribers`}
        </button>
      </div>
      {msg && <div data-testid="newsletter-message" className={`mt-3 text-sm ${msg.err ? "text-[#8B2E14]" : "text-[#1F7A3A]"}`}>{msg.text}</div>}
      <History rows={history} />
    </section>
  );
};
